import type { LoginChallenge } from '@/lib/types/auth.types';

/**
 * The in-flight login handed from step 1 (`LoginPage`) to step 2
 * (`TwoFactorPage`).
 */
export interface PendingLogin {
  challenge: LoginChallenge;
  /**
   * Address entered at step 1. Display fallback only, for when the backend
   * omits the masked `hint` - resend is keyed on the token, never the address.
   */
  email: string;
  /** Landing path after verification; already validated by `LoginPage`. */
  returnTo?: string;
}

/**
 * Module-scoped, in-memory only. This is the whole point of the module:
 *
 * `preAuthToken` is a half-authenticated credential, so where it lives is a
 * security decision, not a convenience one (`docs/specs/features/identity-access/02-design.md` §11 decisions 2-3).
 * Three storage options are deliberately excluded:
 *
 * - **`lib/auth/session.ts`** - that is the token the axios interceptor
 *   attaches to every request, so a pre-auth credential there would ride along
 *   on unrelated calls.
 * - **`localStorage`/`sessionStorage`** - would extend the window in which the
 *   credential sits on the device for no user benefit.
 * - **React Router `location.state`** - looks like memory but is not. React
 *   Router serialises it into `history.state`, which the browser restores on
 *   reload, so a reload mid-2FA would resume the flow instead of restarting it.
 *   This is the trap the original design walked into.
 *
 * A module variable is the only one of the four that genuinely dies with the
 * document: a reload re-evaluates the module and the slot is empty, so the
 * guard on `TwoFactorPage` fires and the user starts over - which is the
 * intended behaviour, not a defect to work around.
 */
let pendingLogin: PendingLogin | null = null;

/** Record the challenge from a successful step 1. Overwrites any previous one. */
export function setPendingLogin(pending: PendingLogin): void {
  pendingLogin = pending;
}

/**
 * Non-destructive read. `TwoFactorPage` reads this on every render (the code
 * submit AND the resend both need the token for the component's whole
 * lifetime), so this must NOT clear the slot on read - it previously did
 * ("single-use, take-on-read"), which broke under React 18 StrictMode's dev
 * mount->unmount->remount simulation: that simulation genuinely tears down
 * and recreates the component's hook state (including any `useRef`), so a
 * "lazy-ref, run once" guard around a destructive read does NOT survive it -
 * the second (real, fresh) mount finds nothing and bounces the user back to
 * `/login`. See `clearPendingLogin` for the actual one-time-use enforcement,
 * which doesn't have this problem because it fires on a real business event
 * instead of a render/mount.
 */
export function getPendingLogin(): PendingLogin | null {
  return pendingLogin;
}

/**
 * Explicit teardown, called once real second-factor verification succeeds
 * (`useVerifyTwoFactor`'s `onSuccess`) - not on read, not on mount/unmount.
 * A spent step still cannot be re-entered: `useVerifyTwoFactor` navigates
 * with `replace`, which removes `/login/verify` from history outright, and
 * this clears the data besides. Also exists for callers that abandon a login
 * before step 2 completes, and to isolate tests from each other.
 */
export function clearPendingLogin(): void {
  pendingLogin = null;
}
